import React, { useId, useState } from 'react';
import { DownOutlined } from '@ant-design/icons';
import styled from 'styled-components';

import { ResponseSection, WeeklyUpdateReportBlock } from '../../types';
import { ResponseActionButton, ResponseActions, ResponseIcon } from '../ResponseActionButton';
import { useResponseAction } from '../responseActions';
import {
  ResponseIntro,
  ResponsePanel,
  ResponsePanelBody,
  ResponsePanelTitle,
  ResponseReply,
  ResponseRow,
  ResponseRows
} from '../responsePrimitives';
import { responseType, rsColor, rsv } from '../responseScale';

// 6265:4762: title over subtitle, the head action at the right.
const Head = styled.header`
  display: flex;
  align-items: center;
  padding: ${rsv.space('weeklyHead')};
  border-bottom: 1px solid ${rsColor.border};
  color: ${rsColor.text};

  > * + * {
    margin-left: ${rsv.space('headIconGap')}px;
  }

  > [data-icon] .anticon {
    font-size: ${rsv.size('headIcon')}px;
  }
`;

const HeadText = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

const Subtitle = styled.p`
  margin: ${rsv.space('subtitleGap')}px 0 0;
  color: ${rsv.color('label')};
  ${responseType('subtitle')}
`;

const Sections = styled.div`
  ${ResponseRows} + & {
    margin-top: ${rsv.space('rowsToSections')}px;
  }

  > * + * {
    margin-top: ${rsv.space('sectionGap')}px;
  }
`;

// 6265:4780.
const SectionBox = styled.div`
  border: 1px solid ${rsColor.border};
  border-radius: ${rsv.radius('row')}px;
  background: ${rsColor.inset};
`;

const SectionHead = styled.button`
  display: flex;
  align-items: center;
  box-sizing: border-box;
  width: 100%;
  min-height: ${rsv.size('sectionHead')}px;
  padding: ${rsv.space('sectionHead')};
  border: none;
  border-radius: ${rsv.radius('row')}px;
  background: transparent;
  color: ${rsColor.text};
  font-family: inherit;
  text-align: left;
  cursor: pointer;
  ${responseType('sectionTitle')}

  > * + * {
    margin-left: ${rsv.space('headIconGap')}px;
  }

  &:focus-visible {
    outline: 2px solid ${rsColor.link};
    outline-offset: -2px;
  }
`;

// 6265:4783 points down when open; 6265:4804 turns it a quarter when closed.
const Chevron = styled.span<{ $open: boolean }>`
  display: inline-flex;
  flex: 0 0 auto;
  color: ${rsColor.muted};
  font-size: ${rsv.size('chevron')}px;
  transform: rotate(${({ $open }) => ($open ? 0 : -90)}deg);
`;

const SectionTitle = styled.span`
  flex: 1 1 auto;
  min-width: 0;
`;

const Count = styled.span`
  flex: 0 0 auto;
  color: ${rsv.color('label')};
  ${responseType('count')}
`;

const SectionBody = styled.p`
  margin: 0;
  padding: ${rsv.space('sectionBody')};
  color: ${rsColor.secondary};
  white-space: pre-line;
  ${responseType('sectionBody')}
`;

// 6265:4792: disc items, the text 57.5 in from the box.
const SectionList = styled.ul`
  margin: ${rsv.space('sectionListGap')}px 0 0;
  padding: ${rsv.space('sectionList')};
  color: ${rsColor.secondary};
  ${responseType('sectionBody')}

  > li + li {
    margin-top: ${rsv.space('sectionListGap')}px;
  }
`;

const Actions = styled(ResponseActions)`
  margin-top: ${rsv.actionsTop('sectionsToActions')}px;
`;

/** One accordion; it opens as the payload says and then toggles locally. */
const Section: React.FC<{ section: ResponseSection }> = ({ section }) => {
  const [open, setOpen] = useState(section.expanded === true);
  const contentId = useId();
  const items = section.items ?? [];
  const hasContent = Boolean(section.body) || items.length > 0;
  return (
    <SectionBox data-testid="orbit-response-section" data-section-id={section.id}>
      <SectionHead
        type="button"
        aria-expanded={open}
        aria-controls={open && hasContent ? contentId : undefined}
        onClick={() => setOpen(current => !current)}
      >
        <Chevron $open={open} aria-hidden="true">
          <DownOutlined />
        </Chevron>
        <SectionTitle>{section.title}</SectionTitle>
        {section.count !== null && section.count !== undefined ? (
          <Count>{section.count}</Count>
        ) : null}
      </SectionHead>
      {open && hasContent ? (
        <div id={contentId}>
          {section.body ? <SectionBody>{section.body}</SectionBody> : null}
          {items.length > 0 ? (
            <SectionList>
              {items.map((item, index) => (
                // Items carry no id; their position in the list is their identity.
                // eslint-disable-next-line react/no-array-index-key
                <li key={index}>{item}</li>
              ))}
            </SectionList>
          ) : null}
        </div>
      ) : null}
    </SectionBox>
  );
};

/** Figma 6423:11257: the Weekly Update panel (6265:4761). */
const WeeklyUpdateReport: React.FC<{ block: WeeklyUpdateReportBlock }> = ({ block }) => {
  const emit = useResponseAction(block);
  return (
    <ResponseReply data-testid="orbit-response-weekly_update_report" data-block-id={block.id}>
      {block.intro ? <ResponseIntro>{block.intro}</ResponseIntro> : null}
      <ResponsePanel>
        <Head>
          <ResponseIcon icon={block.icon} />
          <HeadText>
            <ResponsePanelTitle>{block.title}</ResponsePanelTitle>
            {block.subtitle ? <Subtitle>{block.subtitle}</Subtitle> : null}
          </HeadText>
          {block.head_action ? (
            <ResponseActionButton action={block.head_action} onAction={emit} iconOnly />
          ) : null}
        </Head>
        <ResponsePanelBody>
          {block.rows.length > 0 ? (
            <ResponseRows>
              {block.rows.map(row => (
                <ResponseRow key={row.label} row={row} />
              ))}
            </ResponseRows>
          ) : null}
          {block.sections.length > 0 ? (
            <Sections>
              {block.sections.map(section => (
                <Section key={section.id} section={section} />
              ))}
            </Sections>
          ) : null}
          <Actions actions={block.actions} onAction={emit} />
        </ResponsePanelBody>
      </ResponsePanel>
    </ResponseReply>
  );
};

export default WeeklyUpdateReport;
